/**
 * ISO 3166-2 subdivision options for DE, AT, CH.
 * Used for the location "state" field (Bundesland / Kanton).
 */

export const COUNTRY_NAMES_WITH_STATES = [
  "Deutschland",
  "Österreich",
  "Schweiz",
] as const;

const COUNTRY_NAME_TO_CODE: Record<string, "DE" | "AT" | "CH"> = {
  Deutschland: "DE",
  Österreich: "AT",
  Schweiz: "CH",
};

export function getCountryCodeFromName(
  name: string
): "DE" | "AT" | "CH" | null {
  const code = COUNTRY_NAME_TO_CODE[name];
  return code ?? null;
}

export function hasStateForCountry(countryName: string): boolean {
  return getCountryCodeFromName(countryName) !== null;
}

/** ISO 3166-2 codes and German labels for German Bundesländer */
export const STATE_OPTIONS_DE: { value: string; label: string }[] = [
  { value: "DE-BW", label: "Baden-Württemberg" },
  { value: "DE-BY", label: "Bayern" },
  { value: "DE-BE", label: "Berlin" },
  { value: "DE-BB", label: "Brandenburg" },
  { value: "DE-HB", label: "Bremen" },
  { value: "DE-HH", label: "Hamburg" },
  { value: "DE-HE", label: "Hessen" },
  { value: "DE-MV", label: "Mecklenburg-Vorpommern" },
  { value: "DE-NI", label: "Niedersachsen" },
  { value: "DE-NW", label: "Nordrhein-Westfalen" },
  { value: "DE-RP", label: "Rheinland-Pfalz" },
  { value: "DE-SL", label: "Saarland" },
  { value: "DE-SN", label: "Sachsen" },
  { value: "DE-ST", label: "Sachsen-Anhalt" },
  { value: "DE-SH", label: "Schleswig-Holstein" },
  { value: "DE-TH", label: "Thüringen" },
];

/** ISO 3166-2 codes and German labels for Austrian Bundesländer */
export const STATE_OPTIONS_AT: { value: string; label: string }[] = [
  { value: "AT-1", label: "Burgenland" },
  { value: "AT-2", label: "Kärnten" },
  { value: "AT-3", label: "Niederösterreich" },
  { value: "AT-4", label: "Oberösterreich" },
  { value: "AT-5", label: "Salzburg" },
  { value: "AT-6", label: "Steiermark" },
  { value: "AT-7", label: "Tirol" },
  { value: "AT-8", label: "Vorarlberg" },
  { value: "AT-9", label: "Wien" },
];

/** ISO 3166-2 codes and German labels for Swiss Kantone */
export const STATE_OPTIONS_CH: { value: string; label: string }[] = [
  { value: "CH-AG", label: "Aargau" },
  { value: "CH-AR", label: "Appenzell Ausserrhoden" },
  { value: "CH-AI", label: "Appenzell Innerrhoden" },
  { value: "CH-BL", label: "Basel-Landschaft" },
  { value: "CH-BS", label: "Basel-Stadt" },
  { value: "CH-BE", label: "Bern" },
  { value: "CH-FR", label: "Freiburg" },
  { value: "CH-GE", label: "Genf" },
  { value: "CH-GL", label: "Glarus" },
  { value: "CH-GR", label: "Graubünden" },
  { value: "CH-JU", label: "Jura" },
  { value: "CH-LU", label: "Luzern" },
  { value: "CH-NE", label: "Neuenburg" },
  { value: "CH-NW", label: "Nidwalden" },
  { value: "CH-OW", label: "Obwalden" },
  { value: "CH-SG", label: "Sankt Gallen" },
  { value: "CH-SH", label: "Schaffhausen" },
  { value: "CH-SZ", label: "Schwyz" },
  { value: "CH-SO", label: "Solothurn" },
  { value: "CH-TG", label: "Thurgau" },
  { value: "CH-TI", label: "Tessin" },
  { value: "CH-UR", label: "Uri" },
  { value: "CH-VS", label: "Wallis" },
  { value: "CH-VD", label: "Waadt" },
  { value: "CH-ZG", label: "Zug" },
  { value: "CH-ZH", label: "Zürich" },
];

export const STATE_OPTIONS_BY_COUNTRY: Record<
  "DE" | "AT" | "CH",
  { value: string; label: string }[]
> = {
  DE: STATE_OPTIONS_DE,
  AT: STATE_OPTIONS_AT,
  CH: STATE_OPTIONS_CH,
};

/** All valid state codes for lookup */
const STATE_CODE_TO_LABEL: Record<string, string> = {};
/** German label (normalized) to ISO 3166-2 code, for migrating old DB values */
const STATE_LABEL_TO_CODE: Record<string, string> = {};
for (const opt of [...STATE_OPTIONS_DE, ...STATE_OPTIONS_AT, ...STATE_OPTIONS_CH]) {
  STATE_CODE_TO_LABEL[opt.value] = opt.label;
  STATE_LABEL_TO_CODE[opt.label.trim().toLowerCase()] = opt.value;
}

/**
 * Resolve ISO 3166-2 state code to German display label.
 * Returns the code if not found (e.g. for legacy or unknown codes).
 */
export function getStateLabel(code: string | null | undefined): string {
  if (code == null || code === "") return "";
  return STATE_CODE_TO_LABEL[code] ?? code;
}

/** All valid state codes (for validation) */
export const VALID_STATE_CODES = new Set<string>(
  Object.keys(STATE_CODE_TO_LABEL)
);

export function isValidStateCodeForCountry(
  code: string,
  countryCode: "DE" | "AT" | "CH"
): boolean {
  const options = STATE_OPTIONS_BY_COUNTRY[countryCode];
  return options.some((o) => o.value === code);
}

/**
 * Map a German state/canton name (or existing ISO code) from legacy data to ISO 3166-2 code.
 * Used when migrating from old DB field locationfederalstate.
 * Also used for search: "Bayern, Deutschland" is matched by trying each comma-separated part (e.g. "Bayern").
 */
export function getStateCodeFromLabel(
  labelOrCode: string | null | undefined
): string | null {
  if (labelOrCode == null || labelOrCode.trim() === "") return null;
  const normalized = labelOrCode.trim();
  if (VALID_STATE_CODES.has(normalized)) return normalized;
  const lower = normalized.toLowerCase();
  const code = STATE_LABEL_TO_CODE[lower];
  if (code) return code;
  // Try comma-separated parts so "Bayern, Deutschland" matches "Bayern"
  const parts = normalized.split(",").map((p) => p.trim().toLowerCase()).filter(Boolean);
  for (const part of parts) {
    const partCode = STATE_LABEL_TO_CODE[part];
    if (partCode) return partCode;
  }
  return null;
}
